// contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiService } from '../services/api';
import { StorageService } from '../services/storage';
import { GoogleAuthProvider, FacebookAuthProvider, OAuthProvider, signInWithPopup, signInWithCredential } from 'firebase/auth';
import { auth } from '../services/firebase';
import { Capacitor } from '@capacitor/core';

// ─── Apple Sign-In nonce helpers ────────────────────────────────────────────
// Generates a cryptographically random alphanumeric string used as the nonce.
const generateNonce = (length = 32) => {
  const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._';
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes).map(b => charset[b % charset.length]).join('');
};

// Computes a SHA-256 hex digest using the built-in Web Crypto API.
// Apple receives this hashed version; Firebase receives the raw nonce.
const sha256Hex = async (str) => {
  const encoded = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};
// ────────────────────────────────────────────────────────────────────────────

// Định nghĩa các role
export const ROLES = {
  ADMIN: 'admin',
  OWNER: 'owner',
  MANAGER: 'manager',
  STAFF: 'staff',
  RECEPTIONIST: 'receptionist',
  CUSTOMER: 'customer'
};

// Định nghĩa các permission
export const PERMISSIONS = {
  VIEW_DASHBOARD: 'view_dashboard',
  MANAGE_BOOKINGS: 'manage_bookings',
  MANAGE_STAFF: 'manage_staff',
  MANAGE_EMPLOYEES: 'manage_staff',
  MANAGE_SERVICES: 'manage_services',
  MANAGE_CUSTOMERS: 'manage_customers',
  VIEW_REPORTS: 'view_reports'
};

// Ánh xạ role - permissions
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_STAFF,
    PERMISSIONS.MANAGE_EMPLOYEES,
    PERMISSIONS.MANAGE_SERVICES,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.OWNER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_STAFF,
    PERMISSIONS.MANAGE_EMPLOYEES,
    PERMISSIONS.MANAGE_SERVICES,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.MANAGER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_SERVICES,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.VIEW_REPORTS
  ],
  [ROLES.STAFF]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_CUSTOMERS
  ],
  [ROLES.RECEPTIONIST]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.MANAGE_CUSTOMERS
  ],
  [ROLES.CUSTOMER]: [
    PERMISSIONS.VIEW_DASHBOARD
  ]
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper to derive effective role/permissions from backend user data
  const normalizeUser = (backendUser) => {
    if (!backendUser) return null;

    let role = ROLES.CUSTOMER; // Default for users with no business
    let permissions = ROLE_PERMISSIONS[ROLES.CUSTOMER];

    // Check if user has businesses
    // Note: Backend might return businesses as array of objects
    if (backendUser.businesses && backendUser.businesses.length > 0) {
      // Try to find active context
      const currentBusinessId = localStorage.getItem('currentBusinessId');

      // Expand this if businesses are just IDs or objects
      const activeBusiness = backendUser.businesses.find(b =>
        (typeof b.businessId === 'string' ? b.businessId : b.businessId?._id || b.businessId) === currentBusinessId
      ) || backendUser.businesses[0];

      if (activeBusiness) {
        role = activeBusiness.role;
        // If backend provides specific permissions, use them, else defaults
        if (activeBusiness.permissions && activeBusiness.permissions.length > 0) {
          permissions = activeBusiness.permissions;
        } else {
          permissions = ROLE_PERMISSIONS[role] || [];
        }

        // Persist active business if not set or different
        if (!currentBusinessId) {
          const bid = typeof activeBusiness.businessId === 'string' ? activeBusiness.businessId : activeBusiness.businessId?._id;
          localStorage.setItem('currentBusinessId', bid);
        }
      }
    } else {
      // Explicitly ensure no Owner/Admin powers if no business
      role = ROLES.CUSTOMER;
      permissions = ROLE_PERMISSIONS[ROLES.CUSTOMER];
    }

    return { ...backendUser, role, permissions };
  };

  const loadUser = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      if (token) {
        const userData = await apiService.getCurrentUser();
        if (userData) {
          setUser(normalizeUser(userData));
        } else {
          // Token invalid
          localStorage.removeItem('authToken');
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);

      // Call Backend API
      const result = await apiService.login({ username, password });

      if (result.token) {
        localStorage.setItem('authToken', result.token);
        // Reload user to get full profile including businesses
        await loadUser();
        return { success: true };
      }

      throw new Error('Đăng nhập thất bại: Không nhận được token');
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Đăng nhập thất bại');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      // Call Backend API
      const result = await apiService.register(userData);

      if (result.token) {
        localStorage.setItem('authToken', result.token);
        await loadUser();
        return { success: true };
      }

      // If register returns success but no token (e.g. verify email), logic might differ
      return { success: true };

    } catch (error) {
      console.error('Register error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLoginResult = async (result) => {
    try {
      const firebaseUser = result.user;
      const token = await firebaseUser.getIdToken();
      const apiResult = await apiService.socialLogin(token);

      if (apiResult.token) {
        localStorage.setItem('authToken', apiResult.token);
        // Set user directly from API result — avoids calling loadUser() which
        // does an extra network fetch that can silently hang on iOS networks.
        if (apiResult.user) {
          setUser(normalizeUser(apiResult.user));
        }
        const isNewUser = apiResult.isNewUser === true;
        const hasNoBusiness = isNewUser || (apiResult.user?.businesses?.length === 0);
        return { success: true, isNewUser, hasNoBusiness };
      }
      return { success: false, error: 'No token received' };
    } catch (e) {
      console.error("Social login handle error", e);
      return { success: false, error: e.message };
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return await handleSocialLoginResult(result);
    } catch (error) {
      console.error("Google login error", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithFacebook = async () => {
    try {
      setLoading(true);
      const provider = new FacebookAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return await handleSocialLoginResult(result);
    } catch (error) {
      console.error("Facebook login error", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithApple = async () => {
    try {
      setLoading(true);
      const isNative = Capacitor.isNativePlatform();

      if (isNative && Capacitor.getPlatform() === 'ios') {
        // ── Native iOS path: bypass Firebase Web SDK entirely ──────────────────
        // Firebase's Web SDK cannot reliably exchange Apple native tokens inside
        // a Capacitor WebView — signInWithCredential hangs indefinitely.
        // Instead, we send the Apple identity token directly to our backend,
        // which verifies it against Apple's public keys (JWKS).

        const rawNonce = generateNonce();
        const hashedNonce = await sha256Hex(rawNonce);

        const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');

        // No timeout here — user may take unlimited time in Apple's UI.
        const appleResponse = await SignInWithApple.authorize({
          clientId: 'com.phanh.app',
          redirectURI: 'https://phanh-application.firebaseapp.com/__/auth/handler',
          scopes: 'email name',
          state: '12345',
          nonce: hashedNonce,
        });

        const identityToken =
          appleResponse.response?.identityToken || appleResponse.identityToken;
        if (!identityToken) {
          throw new Error('Không nhận được Identity Token từ Apple.');
        }

        // Combine given + family name if Apple provided them (first sign-in only)
        const givenName = appleResponse.response?.givenName || '';
        const familyName = appleResponse.response?.familyName || '';
        const fullName = [givenName, familyName].filter(Boolean).join(' ') || undefined;

        // Call backend directly — no Firebase involved.
        const apiResult = await apiService.appleNativeLogin(identityToken, fullName);

        if (apiResult.token) {
          localStorage.setItem('authToken', apiResult.token);
          if (apiResult.user) {
            setUser(normalizeUser(apiResult.user));
          }
          const isNewUser = apiResult.isNewUser === true;
          const hasNoBusiness = isNewUser || (apiResult.user?.businesses?.length === 0);
          return { success: true, isNewUser, hasNoBusiness };
        }
        return { success: false, error: 'Không nhận được token từ server.' };
      } else {
        // ── Web path: use Firebase popup (unchanged) ───────────────────────────
        const provider = new OAuthProvider('apple.com');
        const result = await signInWithPopup(auth, provider);
        return await handleSocialLoginResult(result);
      }
    } catch (error) {
      console.error('Apple login error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      StorageService.clearSession();
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  const hasAnyRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const value = {
    user,
    loading,
    error,
    login,

    register,
    loginWithGoogle,
    loginWithFacebook,
    loginWithApple,
    logout,
    hasPermission,
    hasRole,
    hasAnyRole,
    ROLES,
    PERMISSIONS
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);